import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCode,
  faSliders,
  faShieldHalved,
  faRocket,
  faSatelliteDish,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export type PipelineStage =
  | 'workspace'
  | 'validation'
  | 'optimization'
  | 'deployment'
  | 'monitoring';

interface StageConfig {
  key: PipelineStage;
  label: string;
  icon: IconDefinition;
  to: string;
  desc: string;
}

const stages: StageConfig[] = [
  { key: 'workspace', label: 'Strategy', icon: faCode, to: '/features/workspace', desc: 'Define and version strategy code' },
  { key: 'optimization', label: 'Optimization', icon: faSliders, to: '/features/optimization', desc: 'Search parameter surfaces for stability' },
  { key: 'validation', label: 'Validation', icon: faShieldHalved, to: '/features/validation', desc: 'Cross-asset and walk-forward testing' },
  { key: 'deployment', label: 'Deployment', icon: faRocket, to: '/features/research-pipeline', desc: 'Validated parameters enter production' },
  { key: 'monitoring', label: 'Adaptive Flow', icon: faSatelliteDish, to: '/features/continuous-validation', desc: 'Continuous post-deployment monitoring' },
];

interface PipelineStripProps {
  active?: PipelineStage;
}

const PipelineStrip: FC<PipelineStripProps> = ({ active }) => (
  <section className="py-5 px-4 sm:px-6 lg:px-10 bg-white border-b border-gray-200/60">
    <div className="max-w-[960px] mx-auto">
      <div className="flex items-center justify-center gap-1.5 font-mono text-[11px] flex-wrap">
        {stages.map((stage, i) => {
          const isActive = stage.key === active;
          return (
            <span key={stage.key} className="flex items-center gap-1.5">
              <Link
                to={stage.to}
                title={stage.desc}
                className={`group flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors ${isActive
                  ? 'bg-[#9d4edd]/10 text-[#9d4edd] font-semibold border border-[#9d4edd]/30'
                  : 'text-gray-400 hover:text-gray-700 hover:bg-gray-50 border border-transparent'
                  }`}
              >
                <FontAwesomeIcon
                  icon={stage.icon}
                  className={`text-[10px] ${isActive ? 'text-[#9d4edd]' : 'text-gray-300 group-hover:text-gray-500'}`}
                />
                {stage.label}
              </Link>
              {i < stages.length - 1 && (
                <span className="hidden md:inline text-gray-300 text-[10px]">&rarr;</span>
              )}
            </span>
          );
        })}
      </div>
      <p className="text-center text-[11px] text-gray-400 mt-2">
        Part of the Quanthop strategy research pipeline
      </p>
    </div>
  </section>
);

export default PipelineStrip;
